import type { ApiExportRequest, ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import { getListingImageId, getLocationImages } from '@/helpers/imageRotation';
import api from '@/store/api';
import { athena, atlas } from '@/store/api/instances';
import { locationApi } from '@/store/api/services/locations/location';
import type {
  CreateLocationNoteRequest,
  GetLocationNotesResponse,
  GmbGroup,
  ImageRotation,
  ListingDetailView,
  ListingImage,
  ListingInsightView,
  ListingKeywordView,
  ListingView,
  ListingsSummary,
  LocationCreateNoteResponse,
  MarketingDashboardStats,
  QueueSummary,
  UpdateListingGmbGroupData,
} from '@/types';

interface MarketingListingActionRequest {
  locationId: number;
  user: string;
}

interface BulkMarketingRequest {
  location_ids: number[];
  action: 'AskForReview' | 'Unpublish';
  user?: string;
}

interface BulkMarketingReviewsRequest {
  location_ids: number[];
  operator_id: number;
  action: 'Approve' | 'Reject';
}

export interface BulkMarketingResponse {
  processed_count: number;
  skipped_count: number;
  items: ListingView[];
}

/**
 * RTK Query API for Marketing stats and listings.
 * Uses AthenaService /v2/marketing endpoints.
 */
export const marketingApi = api.injectEndpoints({
  endpoints: (build) => ({
    getListingsStats: build.query<ApiResponse<MarketingDashboardStats>, void>({
      query: () =>
        athena({
          url: '/v2/marketing/summary',
          method: 'GET',
        }),
      providesTags: ['MarketingStats'],
    }),
    getListingsSummary: build.query<ApiResponse<ListingsSummary>, void>({
      query: () =>
        athena({
          url: '/v2/marketing/listings/summary',
          method: 'GET',
        }),
      providesTags: ['MarketingStats'],
    }),
    getQueueSummary: build.query<ApiResponse<QueueSummary>, void>({
      query: () =>
        athena({
          url: '/v2/marketing/queue/summary',
          method: 'GET',
        }),
      providesTags: ['MarketingStats'],
    }),
    getMarketingListings: build.query<ApiResponse<ListingView[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          url: '/v2/marketing/listings',
          method: 'POST',
          data,
        }),
      providesTags: ['MarketingQueue'],
    }),
    getMarketingListing: build.query<ApiResponse<ListingDetailView>, number>({
      query: (locationId) =>
        athena({
          url: `/v2/marketing/listings/${locationId}`,
          method: 'GET',
        }),
      providesTags: (_, __, locationId) => [{ type: 'MarketingListing', id: locationId }],
    }),
    // Atlas's PATCH v2/locations is scoped to the caller's own operator and 404s when a
    // marketing admin edits a listing belonging to a different operator, so this is the only
    // endpoint EditLocation.tsx's marketing flow (showGmbGroup) uses to save at all. Unlike
    // v2/locations it also accepts per-field address updates (street_address/address2/city/
    // state/zipcode/country) or a place_id, resolving/validating whichever is given via Google,
    // and it's the only endpoint that persists GMB Group.
    updateMarketingListing: build.mutation<
      ApiResponse<UpdateListingGmbGroupData>,
      {
        locationId: number;
        gmb_group_id: string | null;
        name?: string;
        merchant_id?: string;
        business_contact_name?: string;
        business_contact_phone?: string;
        profile_completed?: boolean;
        hours?: string[];
        street_address?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipcode?: string;
        country?: string;
        place_id?: string;
      }
    >({
      query: ({ locationId, ...data }) =>
        atlas({
          url: `v2/listings/${locationId}`,
          method: 'PATCH',
          data,
        }),
      invalidatesTags: (_, __, { locationId }) => [
        { type: 'MarketingListing', id: locationId },
        { type: 'Location', id: locationId },
      ],
    }),
    rotateListingImage: build.mutation<
      ApiResponse<unknown>,
      {
        locationId: number;
        imageId: string;
        rotation: ImageRotation;
        scope?: 'listing' | 'location';
      }
    >({
      query: ({ locationId, imageId, rotation, scope = 'listing' }) =>
        atlas({
          url: `v2/${scope === 'location' ? 'locations' : 'listings'}/${locationId}/images/${imageId}`,
          method: 'PATCH',
          data: { rotation },
        }),
      async onQueryStarted(
        { locationId, imageId, rotation },
        { dispatch, getState, queryFulfilled },
      ) {
        const applyRotation = (images?: ListingImage[]) => {
          const image = images?.find((item) => getListingImageId(item) === imageId);
          if (image) image.rotation = rotation;
        };

        const patches = [
          dispatch(
            marketingApi.util.updateQueryData('getMarketingListing', locationId, (draft) => {
              applyRotation(draft.data?.image_urls);
            }),
          ),
          dispatch(
            locationApi.util.updateQueryData('getLocation', locationId, (draft) => {
              if (!draft?.data) return;
              draft.data.images = getLocationImages(draft.data);
              applyRotation(draft.data.images);
            }),
          ),
          ...marketingApi.util
            .selectCachedArgsForQuery(getState(), 'getMarketingListings')
            .map((args) =>
              dispatch(
                marketingApi.util.updateQueryData('getMarketingListings', args, (draft) => {
                  applyRotation(
                    draft.data?.find((row) => row.location_id === locationId)?.image_urls,
                  );
                }),
              ),
            ),
        ];

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
    exportListings: build.mutation<BlobPart, ApiExportRequest>({
      query: ({ type, data }) =>
        athena({
          url: `/v2/marketing/listings/${type}`,
          method: 'POST',
          data,
        }),
    }),
    getMarketingListingNotes: build.query<GetLocationNotesResponse, string>({
      query: (locationId) =>
        athena({
          url: `/v2/marketing/listings/${locationId}/notes`,
          method: 'GET',
        }),
      providesTags: (_, __, location_id) => [{ type: 'MarketingListingNotes', location_id }],
    }),
    getMarketingListingInsights: build.query<ApiResponse<ListingInsightView[]>, string>({
      query: (locationId) =>
        athena({
          url: `/v2/marketing/listings/${locationId}/insights`,
          method: 'GET',
        }),
    }),
    getMarketingListingKeywords: build.query<ApiResponse<ListingKeywordView[]>, string>({
      query: (locationId) =>
        athena({
          url: `/v2/marketing/listings/${locationId}/keywords`,
          method: 'GET',
        }),
    }),
    getGmbGroups: build.query<ApiResponse<GmbGroup[]>, void>({
      query: () =>
        athena({
          url: 'v2/gmb-groups',
          method: 'GET',
        }),
      providesTags: ['GmbGroups'],
    }),
    getGmbGroup: build.query<ApiResponse<GmbGroup>, string>({
      query: (id) =>
        athena({
          url: `v2/gmb-groups/${id}`,
          method: 'GET',
        }),
      providesTags: (_, __, id) => [{ type: 'GmbGroups', id }],
    }),
    createGmbGroup: build.mutation<ApiResponse<GmbGroup>, { name: string }>({
      query: (data) =>
        atlas({
          url: 'v2/gmb-groups',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['GmbGroups'],
    }),
    bulkMarketingAction: build.mutation<ApiResponse<BulkMarketingResponse>, BulkMarketingRequest>({
      query: (data) =>
        atlas({
          url: 'v2/locations/bulk-marketing',
          method: 'POST',
          data,
          showError: true,
        }),
      invalidatesTags: (result, error) =>
        result && !error
          ? [
              'MarketingQueue',
              'MarketingStats',
              ...(result.data?.items ?? []).map((item) => ({
                type: 'MarketingListing' as const,
                id: item.location_id,
              })),
            ]
          : [],
    }),
    bulkMarketingReviews: build.mutation<
      ApiResponse<BulkMarketingResponse>,
      BulkMarketingReviewsRequest
    >({
      query: (data) =>
        atlas({
          url: 'v2/locations/bulk-marketing-reviews',
          method: 'POST',
          data,
          showError: true,
        }),
      invalidatesTags: (_, __, { location_ids }) => [
        'MarketingQueue',
        'MarketingStats',
        ...location_ids.map((id) => ({ type: 'MarketingListing' as const, id })),
      ],
    }),
    publishMarketingListing: build.mutation<void, MarketingListingActionRequest>({
      query: ({ locationId, user }) =>
        atlas({
          url: `v2/locations/${locationId}/marketing`,
          method: 'POST',
          data: { action: 'ask_for_review', user },
          showError: true,
        }),
      invalidatesTags: (_, __, { locationId }) => [
        'MarketingQueue',
        'MarketingStats',
        { type: 'MarketingListing', id: locationId },
      ],
    }),
    unpublishMarketingListing: build.mutation<void, MarketingListingActionRequest>({
      query: ({ locationId, user }) =>
        atlas({
          url: `v2/locations/${locationId}/marketing`,
          method: 'POST',
          data: { action: 'unpublish', user },
          showError: true,
        }),
      invalidatesTags: ['MarketingQueue', 'MarketingStats'],
    }),
    createMarketingListingNote: build.mutation<
      LocationCreateNoteResponse,
      Omit<CreateLocationNoteRequest, 'operator_id'>
    >({
      query: ({ location_id, note_text, author }) =>
        atlas({
          url: `v2/locations/${location_id}/notes`,
          method: 'POST',
          data: { note_text, author },
        }),
      invalidatesTags: (_, __, { location_id }) => [
        { type: 'MarketingListingNotes', location_id: location_id.toString() },
      ],
    }),
  }),
});

export const {
  useGetListingsStatsQuery,
  useGetListingsSummaryQuery,
  useGetQueueSummaryQuery,
  useGetMarketingListingsQuery,
  useGetMarketingListingQuery,
  useExportListingsMutation,
  useGetMarketingListingNotesQuery,
  useCreateMarketingListingNoteMutation,
  useGetMarketingListingInsightsQuery,
  useGetMarketingListingKeywordsQuery,
  useUpdateMarketingListingMutation,
  useRotateListingImageMutation,
  useBulkMarketingActionMutation,
  useBulkMarketingReviewsMutation,
  usePublishMarketingListingMutation,
  useUnpublishMarketingListingMutation,
  useCreateGmbGroupMutation,
  useGetGmbGroupsQuery,
  useGetGmbGroupQuery,
} = marketingApi;
